import React from 'react';
import styled, { css } from 'styled-components';
import { lighten } from 'polished';
import PropTypes from 'prop-types';
import theme from '../../theme';
import WellIcon from '../PageContainer/WellIcon';
import Hamburger from './hamburger';
import topBarLinkShape from '../../shapes/topBarLink';

const TopBarContainer = styled.form`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background-color: ${theme('colors.backgroundLight')};
  z-index: 10;
`;

const TopBarContentContainer = styled.div`
  max-width: ${theme('layout.topBarMaxWidth')};
  margin: auto;
  display: flex;
  justify-content: space-between;
  position: relative;
`;

const TopBarIcon = styled.a`
  margin: ${theme('spacing.large')} 0;
  cursor: pointer;
  padding-left: ${theme('spacing.regular')};
`;

const TopBarLinkStyles = css`
  color: ${theme('colors.accentQuarternary')};
  font-weight: 600;
  background-color: transparent;
  height: 1em;
  cursor: pointer;
  justify-content: center;
  margin: auto;
  font-size: 16px;
  padding-right: ${theme('spacing.huge')};
  @media (max-width: ${theme('layout.pageWidth')}) {
    padding-right: ${theme('spacing.regular')};
  }
  &:hover {
    color: ${theme('colors.accentQuarternary').apply(col => lighten(0.2, col))};
  }
  &:active {
    color: ${theme('colors.accentQuarternary')};
  }
`;

const StyledLink = styled.a`
  ${TopBarLinkStyles};
  &:visited {
    color: ${theme('colors.accentQuarternary')};
  }
  text-decoration: none;
`;

const StyledButton = styled.button`
  ${TopBarLinkStyles};
  &:disabled {
    color: ${theme('colors.accentQuarternary').apply(col => lighten(0.2, col))};
    cursor: default;
  }
  outline: none;
`;

const TopBarLink = ({
  id,
  href,
  text,
  cssClass,
  verb,
  disabled,
  onClick,
}) => {
  const Elem = verb === 'GET' ? StyledLink : StyledButton;
  const url = verb === 'GET' ? href : undefined;
  const formAction = verb === 'POST' ? href : undefined;
  const disableButton = verb === 'POST' ? disabled === true : undefined;
  const type = verb === 'POST' ? 'submit' : undefined;
  return url === '#' ? (
    <Elem
      id={id}
      formAction={formAction}
      disabled={disableButton}
      className={cssClass}
      onClick={onClick}
      type={type}
    >
      {text}
    </Elem>
  ) : (
    <Elem
      id={id}
      formAction={formAction}
      href={url}
      disabled={disableButton}
      className={cssClass}
      onClick={onClick}
      type={type}
    >
      {text}
    </Elem>
  );
};
TopBarLink.propTypes = {
  id: PropTypes.string,
  href: PropTypes.string,
  text: PropTypes.string,
  cssClass: PropTypes.string,
  verb: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};
TopBarLink.defaultProps = {
  id: undefined,
  href: '',
  text: '',
  verb: 'GET',
  cssClass: undefined,
  disabled: undefined,
  onClick: () => {},
};

const TopBarRightContentContainer = styled.nav`
  display: flex;
  margin-left: auto;
  padding-right: ${theme('spacing.regular')};

  @media (max-width: ${theme('layout.pageWidth')})  {
    position: fixed;
    top: 99px;
    width: 100%;
    display: none;
    flex-direction: column;
    > * {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 0px;
      height: ${theme('spacing.huge')};
      box-sizing: border-box;
    }
  }
`;

const TopBarComponent = ({
  onIconClick,
  iconHref,
  links,
  icon,
  hideMobileMenu,
}) => (
  <TopBarContainer method="post">
    <button type="submit" disabled style={{ display: 'none' }} aria-hidden="true" />
    <TopBarContentContainer>
      <TopBarIcon onClick={onIconClick} href={iconHref}>
        {icon}
      </TopBarIcon>
      {!hideMobileMenu && <Hamburger />}
      <TopBarRightContentContainer>
        { links.length ? links.map((link) => (
          <TopBarLink
            key={link.href}
            {...link}
          />
        )) : null }
      </TopBarRightContentContainer>
    </TopBarContentContainer>
  </TopBarContainer>
);

TopBarComponent.propTypes = {
  onIconClick: PropTypes.func,
  iconHref: PropTypes.string,
  links: PropTypes.arrayOf(topBarLinkShape),
  icon: PropTypes.element,
  hideMobileMenu: PropTypes.bool,
};

TopBarComponent.defaultProps = {
  onIconClick: () => {},
  iconHref: '/',
  links: [],
  icon: <WellIcon />,
  hideMobileMenu: false,
};

export const getThemeTopBarHeight = ({ theme: t }) => `${(parseFloat(t.spacing.large) * 2) + 32}px`;

export default TopBarComponent;
